import React, { useState, useCallback, useEffect } from 'react';
import { format } from 'date-fns';
import TableComponent from '../../../components/Table/TableComponent';
import SearchIcon from '@mui/icons-material/Search';
import { TableRow, TableCell } from '@mui/material';
import { useAuth } from '../../../context/AuthContext';
import { fetchAllergies } from '../../../services/SubjectiveServices/allergyformService';
import TablePaginationComponent from '../../../components/TablePaginationComponent';

import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';
import IconButton, { ClearButton } from '../../../components/Buttons/SubjectiveIcons';
import { Allergy } from '../../../components/interface/SubjectiveInterfaces/AllergyInterfaces';
import SnackbarMessage from '../../../components/SnackBAr/SnackbarNotification';
import LoadingAnimation from '../../../components/LoadingAnimation';





export interface AllergyFormProps {
    patientId: string
}

const AllergyForm: React.FC<AllergyFormProps> = ({ patientId }) => {
    const { token } = useAuth();
    const [allergies, setAllergies] = useState<Allergy[]>([]);
    const [totalAllergies, setTotalAllergies] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
    const [isButtonEnabled, setIsButtonEnabled] = useState(false);

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [snackbarSeverity, setSnackbarSeverity] = useState<
        'success' | 'info' | 'warning' | 'error'
    >('success')

    const [showClearFilterButton, setShowClearFilterButton] = useState(false)

    const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const fetchAllergiesData = useCallback(async (initial = false) => {
        setLoading(true); // Show loading spinner
        setError('');
        try {
            // Introduce a 1-second delay before fetching the data
            await new Promise((resolve) => setTimeout(resolve, 300));

            const params: Record<string, any> = {
                limit: rowsPerPage,
                page: page + 1,
                order_by: 'values->date',
                dir: 2,
                user_id: patientId,
                slug: 'allergy',
                searchkey: searchTerm,
                slug_array: ''
            };

            if (!initial) {
                params.from = dateRange.start;
                params.to = dateRange.end;
            }

            const data = await fetchAllergies(token!, params);
            const fetchedAllergies = data.data.patient_healths.map(item => ({
                patient_id: patientId,
                slug: item.values?.name?.slug || '',
                id: item.id,
                freeze: item.freeze || 0,
                values: {
                    name: {
                        id: item.values?.name?.id || '',
                        attributes: {
                            allergy_type: item.values?.name?.attributes?.allergy_type || '',
                            allergy_category: item.values?.name?.attributes?.allergy_category || '',
                        },
                        master_type_slug: item.values?.name?.master_type_slug || '',
                        name: item.values?.name?.name || '',
                        slug: item.values?.name?.slug || '',
                        is_active: item.values?.name?.is_active || 1,
                    },
                    type: item.values?.type || '',
                    category: item.values?.category || '',
                    reaction: {
                        id: item.values?.reaction?.id || 0,
                        attributes: item.values?.reaction?.attributes || '',
                        master_type_slug: item.values?.reaction?.master_type_slug || '',
                        name: item.values?.reaction?.name || '',
                        slug: item.values?.reaction?.slug || '',
                        is_active: item.values?.reaction?.is_active || 1,
                    },
                    severity: item.values?.severity || 'Unknown',
                    is_active: item.values?.is_active || 0,
                    date: item.values?.date || '',
                    other_reaction: item.values?.other_reaction || '',
                    treated_by: item.values?.treated_by || '',
                    treatment: item.values?.treatment || '',
                },
            }));

            setAllergies(fetchedAllergies);
            setTotalAllergies(data.data.pagination.total || 0);

            setShowClearFilterButton(!initial || searchTerm.trim() !== '' || (dateRange.start !== '' && dateRange.end !== ''));
        } catch (error) {
            setError('Failed to fetch allergies.');
            console.error('Error fetching allergies', error);
            showSnackbar('Failed to fetch allergies.', 'error');
        } finally {
            setLoading(false); // Hide loading spinner
        }
    }, [searchTerm, dateRange, page, rowsPerPage, patientId]);



    useEffect(() => {
        fetchAllergiesData(true);
    }, [fetchAllergiesData]);

    useEffect(() => {
        const isSearchable = searchTerm.trim() !== '' || (dateRange.start !== '' && dateRange.end !== '');
        setIsButtonEnabled(isSearchable);
    }, [searchTerm, dateRange]);

    const handleSearch = useCallback(() => {
        if (dateRange.start && dateRange.end) {
            setPage(0);
            setLoading(true); // Show spinner while searching
            fetchAllergiesData().finally(() => setLoading(false)); // Hide spinner after search completes
        } else {
            console.warn('Please select both from date and to date.');
            showSnackbar('Please select both from date and to date.', 'warning');
        }
    }, [fetchAllergiesData, dateRange]);

    const handleButtonClick = () => {
        handleSearch();

    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setDateRange({ start: '', end: '' });
        setPage(0);
        fetchAllergiesData(true);
    };

    const formatDate = (dateString: string | number | Date) => {
        return format(new Date(dateString), 'dd-MM-yyyy');
    };
    const renderRow = (item: Allergy, index: number) => {
        console.log(item);
        // Define the severity color based on item.values.severity
        const severityStyles: Record<string, { backgroundColor: string; color: string }> = {
            Mild: { backgroundColor: '#b2f2a0', color: '#000' },      // Updated color for Mild
            Moderate: { backgroundColor: '#ffeb3b', color: '#000' },  // Light text for Moderate
            Severe: { backgroundColor: '#f44336', color: '#FFF' },    // White text for Severe
            Unknown: { backgroundColor: '#ccc', color: '#000' },      // Dark text for Unknown
        };
        const { backgroundColor, color } = severityStyles[item.values.severity] || severityStyles.Unknown;


        return (
            <TableRow key={item.id || index}>
                <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{formatDate(item.values.date)}</TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.values.name.name}</TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.values.name.attributes.allergy_category}</TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>
                    {typeof item.values.reaction.name === 'string'
                        ? item.values.reaction.name
                        : JSON.stringify(item.values.reaction.name)}
                </TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>
                    <span
                        style={{
                            backgroundColor: backgroundColor,
                            color: color,
                            padding: '4px 8px',
                            borderRadius: '8px',
                        }}
                    >
                        {item.values.severity}
                    </span>
                </TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }} >
                    {item.values.is_active === 1 ? 'Active' : 'Inactive'}
                </TableCell>

            </TableRow>
        );
    };


    return (
        <div>
            <div className="flex flex-wrap items-center gap-4 w-full pb-4">
                <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                    <DateRangePicker
                        startDateId="allergy_Start_Date"
                        endDateId="allergy_End_Date"
                        value={{ startDate: dateRange.start, endDate: dateRange.end }}
                        onChange={dates => {
                            setDateRange({ start: dates.startDate || '', end: dates.endDate || '' });
                        }}
                    />
                    <div className="flex items-center gap-2 shrink-0">
                        <IconButton
                            onClick={handleButtonClick}
                            disabled={!isButtonEnabled}
                            icon={<SearchIcon />}
                        />
                        {showClearFilterButton && (
                            <ClearButton onClick={handleClearFilters} />
                        )}
                    </div>
                </div>
            </div>
            {loading ? (
    <div className="text-center my-4">
        <LoadingAnimation />
    </div>
) : (
    <div>
        <TableComponent
            headers={['S.No', 'Onset Date', 'Allergy Name', 'Category', 'Reaction', 'Severity', 'Status']}
            data={allergies}
            loading={loading}
            error={error}
            renderRow={renderRow}
        />
    </div>
)}


            <TablePaginationComponent
                count={totalAllergies}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_event, newPage) => {
                    setPage(newPage);
                }}
                onRowsPerPageChange={(event) => {
                    const newRowsPerPage = parseInt(event.target.value, 10);
                    setRowsPerPage(newRowsPerPage);
                    setPage(0);
                }}
            />
            {/* Snackbar Component */}
            <SnackbarMessage
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setSnackbarOpen(false)}
            />

        </div>
    );
};

export default AllergyForm





